import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Animals.css";

import parrotImg from "../assets/animals/parrot.png";
import blueMagpieImg from "../assets/animals/bluemagpie.png";
import butterflyImg from "../assets/animals/butterfly.png";

import monkeyImg from "../assets/animals/monkey.png";
import squirrelImg from "../assets/animals/squirrel.png";
import peacockImg from "../assets/animals/peacock.png";

import elephantImg from "../assets/animals/elephant.png";
import tigerImg from "../assets/animals/tiger.png";
import lizardImg from "../assets/animals/lizard.png";

import cloudImg from "../assets/animals/cloud.png";
import branchImg from "../assets/animals/branch.png";
import bushImg from "../assets/animals/bush.png";

import skyBg from "../assets/animals/sky.png";
import treesBg from "../assets/animals/trees.png";
import landBg from "../assets/animals/land.png";

// all the habitatt groups and their animals are stored here as a constant
// each key matches the string passed into chooseHabitat() so it can be looked up diretcly
const HABITAT_DATA = {
  sky: {
    title: "Sky Animals",
    subtitle: "Click the clouds to reveal the hidden sky animal!",
    coverImage: cloudImg,
    backgroundImage: skyBg,
    animals: [
      {
        id: "parrot",
        name: "Sri Lanka Hanging Parrot",
        image: parrotImg,
        fact: "It's called a hanging bird because it sleeps upside down like a bat!",
      },
      {
        id: "blue-magpie",
        name: "Sri Lanka Blue Magpie",
        image: blueMagpieImg,
        fact: "Sri Lankan Blue Magpies are only found in Sri Lanka's rainforests",
      },
      {
        id: "butterfly",
        name: "Yellow Orange Tip Butterfly",
        image: butterflyImg,
        fact: "The orange colour camouflage with dead leaves and flowers, to help it hide from predators.",
      },
    ],
  },

  trees: {
    title: "Tree Animals",
    subtitle: "Tap the leaves to uncover animals hiding in the trees!",
    coverImage: branchImg,
    backgroundImage: treesBg,
    animals: [
      {
        id: "monkey",
        name: "Toque Macaque Monkey",
        image: monkeyImg,
        fact: "They have a swirl on their head that looks like a 'Toque Macaque' hat.",
      },
      {
        id: "squirrel",
        name: "Sri Lankan Palm Squirrel",
        image: squirrelImg,
        fact: "These squirrels are omniviourous, meaning they eat plants and small insects too",
      },
      {
        id: "lizard",
        name: "Sri Lankan Lizard",
        image: lizardImg,
        fact: "They can change colour depending on the temperature or mood, to help it blend with the forsest",
      },
    ],
  },

  land: {
    title: "Land Animals",
    subtitle: "Tap the bushes to discover animals on the ground!",
    coverImage: bushImg,
    backgroundImage: landBg,
    animals: [
      {
        id: "elephant",
        name: "Sri Lankan Elephant",
        image: elephantImg,
        fact: "Elephants gather together in Sri Lanka, and form on of the largest meetings of wild elephants in the world",
      },
      {
        id: "tiger",
        name: "Sri Lankan Leapord",
        image: tigerImg,
        fact: "Yala National Park is one of the best places in the world to see Sri Lankan Leapords ",
      },
      {
        id: "peacock",
        name: "Indian Peacock",
        image: peacockImg,
        fact: "Male Peacocks can shake their giant feathers to create a beautiful dance and a rattling sound",
      },
    ],
  },
};

// count of gow many cover tiles sit on top of the animal image
// when all of them are removed the animal fact shows up
const TILE_COUNT = 8;

// builds an array of numbers 0 to TILE_COUNT-1
function buildTiles() {
  return Array.from({ length: TILE_COUNT }, (_, i) => i);
}

export default function Animals() {
  const navigate = useNavigate();

  // which habitat the user picked, null means they havent picked yet
  const [selectedHabitat, setSelectedHabitat] = useState(null);

  // which animal inside the current habitat is showing
  const [animalIndex, setAnimalIndex] = useState(0);

  // tracks which pieces have been clicked and removed by the user
  const [clearedTiles, setClearedTiles] = useState([]);

  // once all pieces are gone this turns to true and the fact card shows
  const [factVisible, setFactVisible] = useState(false);

  // ger the current habitat object and current animal from state
  const currentHabitat = selectedHabitat ? HABITAT_DATA[selectedHabitat] : null;
  const currentAnimal = currentHabitat?.animals[animalIndex];

  const tiles = useMemo(() => buildTiles(), []);

  // called when the user taps a habitat card on the chooser screen
  function chooseHabitat(habitat) {
    setSelectedHabitat(habitat);
    setAnimalIndex(0);
    setClearedTiles([]);
    setFactVisible(false);
  }

  //called when the user clicks a cover tile — removes it and checks if all are gone
  function removeTile(index) {
    // does nothing if this tile was already removed
    if (clearedTiles.includes(index)) return;

    const next = [...clearedTiles, index];
    setClearedTiles(next);

    // if every tile is now removed, time to show the fact card
    if (next.length === tiles.length) {
      setFactVisible(true);
    }
  }

  // moves to the next animal, or goes back to the habitat chooser after the last one
  function goToNext() {
    if (!currentHabitat) return;

    const onLastAnimal = animalIndex === currentHabitat.animals.length - 1;

    if (onLastAnimal) {
      // all animals in this habitat done, go back to the picker
      setSelectedHabitat(null);
      return;
    }

    // advance to the next animal and reset the tile/fact state
    setAnimalIndex((prev) => prev + 1);
    setClearedTiles([]);
    setFactVisible(false);
  }

  return (
    <div className="animalsPage">
      <div className="animalsContent">

        <div className="animalsHeaderTop">
          <button
            className="quizHomeBtn"
            onClick={() => navigate("/loading", { state: { to: "/home" } })}
            aria-label="Go Home"
            title="Go Home"
            type="button"
          >
            {/* inline svg icon*/}
            <svg
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="quizHomeBtn__icon"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 0L0 6V8H1V15H4V10H7V15H15V8H16V6L14 4.5V1H11V2.25L8 0ZM9 10H12V13H9V10Z"
                fill="#ffffff"
              />
            </svg>
          </button>
        </div>

        <h1 className="animalsTitle">Animals of Sri Lanka</h1>
        <p className="animalsSubtitle">
          Pick a place and reveal the hidden animals!
        </p>

        {/* habitat chooser is only shown when no habitat is selected yet */}
        {!selectedHabitat && (
          <div className="habitatChooser">
            <button
              className="habitatCard"
              onClick={() => chooseHabitat("sky")}
              type="button"
            >
              <h2>Sky</h2>
              <p>Find animals hiding behind the clouds.</p>
              <div className="habitatImageCard">
                <img src={skyBg} alt="Sky habitat" className="habitatImage" />
              </div>
            </button>

            <button
              className="habitatCard"
              onClick={() => chooseHabitat("trees")}
              type="button"
            >
              <h2>Trees</h2>
              <p>Look behind the leaves for tree animals.</p>
              <div className="habitatImageCard">
                <img src={treesBg} alt="Trees habitat" className="habitatImage" />
              </div>
            </button>

            <button
              className="habitatCard"
              onClick={() => chooseHabitat("land")}
              type="button"
            >
              <h2>Land</h2>
              <p>Tap the bushes to reveal animals on the ground.</p>
              <div className="habitatImageCard">
                <img src={landBg} alt="Land habitat" className="habitatImage" />
              </div>
            </button>
          </div>
        )}

        {/* game panel is only shown when a habitat is seleccted and an animal exists */}
        {selectedHabitat && currentAnimal && (
          <div className="animalPanel">
            <h2 className="animalPanelTitle">{currentHabitat.title}</h2>
            <p className="animalPanelSubtitle">{currentHabitat.subtitle}</p>

            {/* the stage holds the background image, the animal image, and all the tiles */}
            <div
              className="animalRevealStage"
              style={{ backgroundImage: `url(${currentHabitat.backgroundImage})` }}
            >
              {/* animal image starts dim and small, the "revaaled" class makes it pop */}
              <img
                src={currentAnimal.image}
                alt={currentAnimal.name}
                className={`animalRevealImage ${factVisible ? "revealed" : ""}`}
              />

              {/* render one button per piecve, clicking it calls removeTile with its index */}
              {tiles.map((tile) => (
                <button
                  key={tile}
                  className={`coverPiece ${clearedTiles.includes(tile) ? "removed" : ""}`}
                  onClick={() => removeTile(tile)}
                >
                  <img
                    src={currentHabitat.coverImage}
                    alt=""
                    className="coverImage"
                  />
                </button>
              ))}
            </div>

            {/* fact card slides in once all tiles have been removed */}
            {factVisible && (
              <div className="animalFactCard">
                <div className="animalFactBadge">You found it!</div>
                <h3>{currentAnimal.name}</h3>
                <p>{currentAnimal.fact}</p>

                <div className="animalActions">
                  {/* button label changes depending on whether this is the last animal */}
                  <button className="continueButton" onClick={goToNext}>
                    {animalIndex === currentHabitat.animals.length - 1
                      ? "Choose Another Place"
                      : "Next Animal"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}